// FLEET · EXPEDITIONS and the start sheet (mockup round 091 card 03,
// R1893; ui-map-interface.md §12 "FLEET · EXPEDITIONS").
//
// The page has two parts under a separator head each:
//
// - UNDERWAY: every running dive and every dive in the build queue, as the
//   note row of FLEET · MISSIONS (`noteRowHtml`). A running dive states a
//   progress bar, the return clock in world time and the time left. A dive
//   whose outfit job holds a build slot states that it outfits, the return
//   clock and the time left (R1899); a dive whose outfit job waits states
//   that it is in the build queue. The aside counts the mission slots,
//   because a dive holds one (R680).
// - START A DIVE: the depths of the dive catalog as specification rows.
//   Line 2 is the yield and the core chance, the figure column the
//   duration over the risk of a captain of Prospecting 0 (R669), and the
//   press START, or the dashed LOCKED with the gate on line 2.
//
// START opens the start sheet, a card of the kit in the context slot: the
// return clock BACK, which counts the wait for a build slot and the outfit
// job (R1899), the yield, the core chance and the risk range once, the free captains as
// captain rows with the Prospecting column lit, the age alone on line 2,
// and the risk, the yield and the Prospecting practice of each captain on
// line 3, then one SEND press that names the selected captain, with the
// note under it so that the press stands high on the card.
//
// USE CORE is not on the page: the core card of the top bar holds it
// (R1458). The module is pure: it reads the state and writes markup.

import { Building, type Captain, type ExpeditionSpec, JobKind } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, dur } from '../../state';
import type { Action, CardSpec } from '../cards/card';
import { esc } from '../esc';
import { FL_SEP, noteRowHtml } from '../fleetlist';
import { diveRiskPct, fillPct } from '../format';
import { hhmm } from '../../locale/clock';
import { buildRowHtml, rowIcon } from './buildrow';
import { attrTrainFigures, practiceStopText } from './captaincard';
import { freeCaptains, pickListHtml } from './cappicker';
import { attrName, captainPlace, type CapRowCtx } from './caprow';
import { diveName, diveSpec } from './markup';
import { ATTR_PROSPECTING, ATTR_SHORT } from './tables';

/**
 * A separator head (round 091): the title with a dot, a rule over it, and
 * an aside at the right. `open` draws the dot hollow, for the part that
 * starts a thing rather than the part that lists what runs.
 */
function sepHeadHtml(title: string, aside: string, open = false): string {
  return `<h3 class="sep"><span><i class="sep-dot${open ? ' o' : ''}"></i>${esc(title)}</span>`
    + (aside ? `<i class="sep-aside">${aside}</i>` : '') + '</h3>';
}

/** The world clock `sec` game-seconds from now, as hh:mm. */
function clockIn(s: GameState, sec: number): string {
  return hhmm(s.worldClockSec() + sec);
}

/** The Dive Control level of the empire. */
const diveControl = (s: GameState): number => s.level(Building.BUILDING_RADIATION_CONTROL);

/**
 * The injury risk one captain runs on one depth (R669, R673): the base of
 * the depth, cut by the empire's Dive Control and the captain's own
 * Prospecting. Null is a dive whose risk is zero, which is never rolled.
 */
function captainRisk(s: GameState, spec: ExpeditionSpec, prospecting: number): string | null {
  return diveRiskPct(spec.injuryPct, diveControl(s), prospecting, s.rules);
}

/** The words of a risk: `9% injury`, or `no injury` for a dive never rolled. */
const riskWords = (pct: string | null): string => pct === null ? S.exp_risk_none : S.exp_risk({ injury: pct });

/** The isotopes one captain brings home on one depth: the catalog yield
 *  lifted by the captain's dive yield bonus, which the server states. */
function captainYield(spec: ExpeditionSpec, c: Captain): number {
  return Math.round(spec.isotopes * (1 + (c.effects?.diveYieldPct ?? 0) / 100));
}

/** The gate of one depth, as the words of line 2, or '' when it is open. */
function diveGate(s: GameState, spec: ExpeditionSpec): string {
  if (diveControl(s) >= spec.radControl && (spec.tech === 0 || s.hasTech(spec.tech))) return '';
  return S.expedition_needs_rad({ level: spec.radControl })
    + (spec.tech ? S.expedition_and_tech({ tech: s.tech(spec.tech)?.name ?? '' }) : '');
}

/** One bar of a note row: the share of a dive done, 0 to 1. */
function progressBar(frac: number): string {
  return `<span class="fl-v prog"><i class="fl-vb"><b style="width:${fillPct(frac)}"></b></i></span>`;
}

/**
 * UNDERWAY: the head with the mission slots, then one note row for every
 * running dive and every dive in the build queue. A dive's time left is
 * NOT queue work, so no queue rate divides it (R688). A dive whose outfit
 * job holds a build slot surfaces when that job lands plus the dive
 * (R1899): the job's ETA is the server's figure. A dive whose outfit job
 * waits states no clock, because no frame states when a waiting job starts.
 */
export function underwayHtml(s: GameState): string {
  const e = s.empire;
  const dives = e?.expeditions ?? [];
  const queued = s.queue.filter((j) => j.kind === JobKind.JOB_KIND_EXPEDITION);
  const slots = e ? S.exp_slots({ n: `<b>${e.missions}</b>`, cap: e.missionCap }) : '';
  let html = sepHeadHtml(S.exp_underway, slots);
  if (dives.length + queued.length === 0) return html + `<div class="empty">${S.exp_underway_none}</div>`;
  for (const x of dives) {
    const total = x.totalSec > 0 ? x.totalSec : diveSpec(s, x.depth)?.durationSec ?? 0;
    const done = total > 0 ? 1 - x.remainingSec / total : 0;
    html += noteRowHtml(x.captainName, diveName(s, x.depth), [
      progressBar(done),
      esc(S.exp_surfaces({ clock: clockIn(s, x.remainingSec) })),
      esc(S.exp_left({ left: dur(x.remainingSec) })),
    ].join(FL_SEP));
  }
  for (const j of queued) {
    const dive = diveSpec(s, j.count);
    const back = j.active && dive ? s.jobEtaSec(j) + dive.durationSec : 0;
    const tail = back > 0
      ? [esc(S.exp_surfaces({ clock: clockIn(s, back) })), esc(S.exp_left({ left: dur(back) }))]
      : [esc(S.exp_in_queue)];
    html += noteRowHtml(s.captain(j.target)?.name ?? '?', diveName(s, j.count),
      [`<span class="ord">${esc(S.exp_outfitting)}</span>`, ...tail].join(FL_SEP));
  }
  return html;
}

/**
 * START A DIVE: the head with the Dive Control level, then one spec row
 * per depth. A gated depth reads LOCKED with its gate on line 2; an open
 * depth with no captain free has a faint press and the reason on line 2.
 * `open` is the depth whose start sheet stands, and its row is lit.
 */
export function startRowsHtml(s: GameState, open: number): string {
  const rad = diveControl(s);
  const free = freeCaptains(s);
  const outfitting = s.queue.some((j) => j.kind === JobKind.JOB_KIND_EXPEDITION);
  let html = sepHeadHtml(S.exp_start,
    rad > 0 ? S.exp_dive_control({ level: `<b>${rad}</b>` }) : esc(S.exp_no_control), true);
  for (const spec of s.catalog?.expeditions ?? []) {
    const gate = diveGate(s, spec);
    const nobody = !gate && free.length === 0
      ? outfitting ? S.expedition_outfitting : S.expedition_no_captain : '';
    // The row states the risk of a captain of Prospecting 0; the start
    // sheet states what each captain changes. The duration is the dive
    // itself and never queue work, so no queue rate divides it (R688).
    const time = dur(spec.durationSec);
    html += buildRowHtml({
      icon: rowIcon('dive'),
      cls: open === spec.depth ? 'picked' : '',
      name: `<span class="name">${esc(spec.name)}</span><span class="dep">${esc(S.exp_depth({ depth: spec.depth }))}</span>`,
      line2: esc(gate || nobody || S.exp_yield({ isotopes: spec.isotopes, core: spec.coreChancePct })),
      side: gate ? `<span class="time">${time}</span>`
        : `<span class="cost">${time}</span><span class="time">${esc(riskWords(captainRisk(s, spec, 0)))}</span>`,
      press: {
        act: 'divestart', id: spec.depth, word: S.btn_start_dive,
        state: gate ? 'lock' : nobody ? 'held' : 'ready',
      },
    });
  }
  return html;
}

/** The captain the start sheet selects first: the lowest risk, which is
 *  the most Prospecting, and the first in roster order on a tie. */
export function bestDiver(free: readonly Captain[]): number {
  let best: Captain | undefined;
  for (const c of free) {
    if (!best || (c.attrs[ATTR_PROSPECTING] ?? 0) > (best.attrs[ATTR_PROSPECTING] ?? 0)) best = c;
  }
  return best?.id ?? 0;
}

/**
 * The Prospecting practice of one captain on line 3 of the start sheet:
 * `PRO 40%` and a short bar of the share of the next practice point
 * (R1478), or the stop of practice in place of both. The title states
 * the whole figure. The practice keeps its whole width at the right end
 * of line 3, and the risk and the yield before it cut with an ellipsis
 * (R1986).
 */
function practiceHtml(s: GameState, c: Captain): string {
  const f = attrTrainFigures(c, ATTR_PROSPECTING, s.rules.captain);
  const stop = practiceStopText(f);
  const attr = ATTR_SHORT[ATTR_PROSPECTING] ?? '';
  const tip = stop || S.caprow_practice({ attr: attrName(s, ATTR_PROSPECTING), pct: f.pct, n: f.practised });
  const fig = esc(S.exp_cap_practice({ attr, fig: stop || S.exp_sum_pct({ pct: String(f.pct) }) }));
  return `<span class="fl-v prog" title="${esc(tip)}">${fig}`
    + (stop ? '' : `<i class="fl-vb"><b style="width:${f.pct}%"></b></i>`) + '</span>';
}

/** One cell of the summary: the cap word, the figure and the small word. */
const sumCell = (key: string, fig: string, what: string): string =>
  `<span><i>${esc(key)}</i><b>${esc(fig)}</b><small>${esc(what)}</small></span>`;

/**
 * The start sheet of one depth (R1893), or null when the depth is gone or
 * gated. BACK is the world clock at which a dive started now surfaces:
 * the wait for a build slot, the outfit job and the dive (R1899). `sel` is the selected captain; a captain no longer free gives way
 * to the best diver. The summary states the yield, the core chance and the
 * risk range of the free captains once; each captain row states that
 * captain's age on line 2 after the state word, with no separator, and the
 * risk, the yield and the Prospecting practice on line 3 (R1986); SEND
 * names the selected captain and sends the dive order of today (`dive`,
 * the depth and the captain). The note stands
 * under SEND, so the press shows without a scroll.
 */
export function startSheetSpec(ctx: CapRowCtx, depth: number, sel: number): CardSpec | null {
  const s = ctx.state;
  const spec = diveSpec(s, depth);
  if (!spec || diveGate(s, spec)) return null;
  const free = freeCaptains(s);
  const best = bestDiver(free);
  const pick = free.find((c) => c.id === sel) ?? free.find((c) => c.id === best);
  const risks = (free.length > 0 ? free : [undefined])
    .map((c) => captainRisk(s, spec, c?.attrs[ATTR_PROSPECTING] ?? 0));
  const nums = risks.filter((r): r is string => r !== null).map(Number);
  const lo = Math.min(...nums), hi = Math.max(...nums);
  const riskFig = nums.length === 0 ? S.exp_sum_none
    : lo === hi ? S.exp_sum_pct({ pct: String(lo) }) : S.exp_sum_range({ lo: String(lo), hi: String(hi) });
  const body = '<div class="ck-sum">'
    + sumCell(S.exp_sum_yield, S.exp_sum_iso_fig({ n: spec.isotopes }), S.exp_sum_isotopes)
    + sumCell(S.exp_sum_core, S.exp_sum_pct({ pct: String(spec.coreChancePct) }), S.exp_sum_core_what)
    + sumCell(S.exp_sum_risk, riskFig, S.exp_sum_by_captain)
    + '</div>'
    + `<h3 class="ck-ph">${esc(S.exp_captains)}<i>${esc(S.pick_free({ n: free.length }))}</i></h3>`
    + pickListHtml(ctx, free.map((c) => ({
      c,
      line2: `<span class="ord">${esc(captainPlace(ctx, c).word)}</span> ${esc(S.caprow_age({ age: c.age }))}`,
      line3: '<span class="xp-l3"><span class="xp-l3t">' + esc(S.exp_cap_line({
        risk: riskWords(captainRisk(s, spec, c.attrs[ATTR_PROSPECTING] ?? 0)),
        isotopes: captainYield(spec, c),
      })) + '</span>' + practiceHtml(s, c) + '</span>',
    })), { lit: ATTR_PROSPECTING, sel: pick?.id ?? 0, act: 'picksel' });
  const send: Action = pick
    ? { act: 'dive', id: pick.id, n: spec.depth, face: 'dark', label: S.btn_send_named({ name: pick.name.toUpperCase() }) }
    : { act: 'dive', face: 'dark', label: S.btn_send_dive, off: true };
  return {
    size: 'card', mark: rowIcon('dive'), title: spec.name, ink: 'catalogue',
    kind: S.exp_sheet_kind({ depth: spec.depth, time: dur(spec.durationSec), clock: clockIn(s, s.diveBackSec(spec.durationSec)) }),
    close: true, closeAct: 'pickclose',
    body,
    note: free.length > 0 ? { lines: [esc(S.exp_sheet_note)], foot: true }
      : { lines: [esc(S.exp_nobody)], tone: 'bad', foot: true },
    actions: [send],
  };
}
